import React, { useState, useEffect } from 'react';
import './App.css';
import { supabase } from './supabase';

function Admin() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [error, setError] = useState('');
  const [activeForm, setActiveForm] = useState(null);
  const [formData, setFormData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // Fixed credentials
  const ADMIN_USERNAME = 'admin';
  const ADMIN_PASSWORD = 'admin123';

  // Form configurations
  const forms = [
    { id: 'orders', name: 'Orders', table: 'book_orders', special: 'orders' },
    { id: 'shop_orders', name: 'Shop Orders', table: 'shop_orders' },
    { id: 'course_enrollments', name: 'Course Enrollments', table: 'course_enrollments' },
    { id: 'consultations', name: 'Consultations', table: 'consultations' },
    { id: 'contact_messages', name: 'Contact Messages', table: 'contact_messages' },
    { id: 'art_custom_requests', name: 'Art Custom Requests', table: 'art_custom_requests' },
    { id: 'art_print_orders', name: 'Art Print Orders', table: 'art_print_orders' },
    { id: 'book_orders', name: 'Book Orders', table: 'book_orders' }
  ];

  useEffect(() => {
    if (isLoggedIn && activeForm) {
      loadFormData(activeForm);
    }
  }, [isLoggedIn, activeForm]);

  const loadFormData = async (formId) => {
    setLoading(true);
    setFetchError(null);
    try {
      const form = forms.find(f => f.id === formId);
      if (!form) {
        setFetchError('Form not found');
        setFormData([]);
        return;
      }

      console.log('Loading data from table:', form.table);
      
      // Special handling for Orders tab - group by order_id
      if (form.special === 'orders') {
        let { data, error } = await supabase
          .from(form.table)
          .select('*')
          .order('created_at', { ascending: false });
        
        if (error && error.message && error.message.includes('column')) {
          const result = await supabase
            .from(form.table)
            .select('*');
          data = result.data;
          error = result.error;
        }
        
        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }
        
        // Group orders by order_id
        if (data && data.length > 0) {
          const groupedOrders = {};
          data.forEach(order => {
            if (!groupedOrders[order.order_id]) {
              groupedOrders[order.order_id] = {
                order_id: order.order_id,
                customer_name: order.customer_name,
                email: order.email,
                mobile: order.mobile,
                address: order.address,
                city: order.city,
                country: order.country,
                total_amount: order.total_amount,
                status: order.status,
                payment_method: order.payment_method,
                created_at: order.created_at,
                updated_at: order.updated_at,
                items: []
              };
            }
            groupedOrders[order.order_id].items.push({
              book_title: order.book_title,
              book_key: order.book_key,
              quantity: order.quantity,
              unit_price: order.unit_price,
              subtotal: order.subtotal
            });
          });
          
          // Convert to array and format for display
          const ordersArray = Object.values(groupedOrders).map(order => ({
            ...order,
            items_display: order.items.map(item => 
              `${item.quantity}x ${item.book_title} ($${item.unit_price} each)`
            ).join('; ')
          }));
          
          setFormData(ordersArray);
        } else {
          setFormData([]);
          setFetchError('No orders found');
        }
        return;
      }
      
      // Regular table loading
      let { data, error } = await supabase
        .from(form.table)
        .select('*')
        .order('created_at', { ascending: false });
      
      // If ordering fails (column might not exist), try without ordering
      if (error && error.message && error.message.includes('column')) {
        console.warn('created_at column not found, loading without ordering');
        const result = await supabase
          .from(form.table)
          .select('*');
        data = result.data;
        error = result.error;
      }
      
      if (error) {
        console.error('Supabase error:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        throw error;
      }
      
      console.log('Data loaded:', data);
      setFormData(data || []);
      if (!data || data.length === 0) {
        setFetchError('No data found in database');
      }
    } catch (error) {
      console.error('Error loading form data:', error);
      setFetchError(error.message || 'Failed to load data. Please check console for details.');
      setFormData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (username === ADMIN_USERNAME && password === ADMIN_PASSWORD) {
      setIsLoggedIn(true);
      setError('');
      // Set first form as active by default
      if (forms.length > 0) {
        setActiveForm(forms[0].id);
      }
    } else {
      setError('Invalid credentials. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setUsername('');
    setPassword('');
    setActiveForm(null);
    setFormData([]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const renderTable = () => {
    if (loading) {
      return (
        <div className="admin-loading">
          <p>Loading data...</p>
        </div>
      );
    }

    if (formData.length === 0) {
      return (
        <div className="admin-empty">
          <p>{fetchError || 'No data found for this form.'}</p>
        </div>
      );
    }

    const form = forms.find(f => f.id === activeForm);
    const columns = formData.length > 0 ? Object.keys(formData[0]).filter(col => col !== 'items') : [];

    return (
      <div className="admin-table-container">
        <div className="admin-table-header">
          <h2>{form?.name || 'Form Data'}</h2>
          <button 
            className="planner-btn admin-refresh-btn"
            onClick={() => loadFormData(activeForm)}
          >
            Refresh
          </button>
        </div>
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col}>{col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {formData.map((row, index) => (
                <tr key={row.order_id || row.id || index}>
                  {columns.map((col) => {
                    const value = row[col];
                    if (col === 'created_at' || col === 'updated_at') {
                      return <td key={col}>{formatDate(value)}</td>;
                    }
                    if (col === 'items_display') {
                      return <td key={col} className="order-items-cell">{String(value)}</td>;
                    }
                    if (typeof value === 'boolean') {
                      return <td key={col}>{value ? 'Yes' : 'No'}</td>;
                    }
                    if (value === null || value === undefined) {
                      return <td key={col}>-</td>;
                    }
                    return <td key={col}>{String(value)}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="admin-table-footer">
          <p>Total Records: {formData.length}</p>
        </div>
      </div>
    );
  };

  if (isLoggedIn) {
    return (
      <div className="main-bg">
        <div className="admin-dashboard-container">
          {/* Sidebar */}
          <div className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
            <div className="admin-sidebar-header">
              <h2>Admin Panel</h2>
              <button 
                className="admin-sidebar-toggle"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                {sidebarOpen ? '←' : '→'}
              </button>
            </div>
            <div className="admin-sidebar-content">
              <div className="admin-user-info">
                <p>Welcome, Admin</p>
              </div>
              <nav className="admin-sidebar-nav">
                <h3>Forms</h3>
                <ul>
                  {forms.map((form) => (
                    <li key={form.id}>
                      <button
                        className={`admin-nav-item ${activeForm === form.id ? 'active' : ''}`}
                        onClick={() => setActiveForm(form.id)}
                      >
                        {form.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="admin-sidebar-footer">
                <button className="planner-btn admin-logout-btn" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="admin-main-content">
            {activeForm ? (
              renderTable()
            ) : (
              <div className="admin-welcome">
                <h2>Welcome to Admin Dashboard</h2>
                <p>Select a form from the sidebar to view submitted data.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-bg">
      <div className="admin-login-container">
        <div className="admin-login-card">
          <div className="admin-login-header">
            <h1>Admin Login</h1>
            <p>Enter your credentials to access the admin panel</p>
          </div>
          
          <form className="admin-login-form" onSubmit={handleLogin}>
            {error && <div className="admin-error">{error}</div>}
            
            <div className="admin-form-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                required
                className="admin-input"
              />
            </div>
            
            <div className="admin-form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                className="admin-input"
              />
            </div>
            
            <button type="submit" className="planner-btn admin-login-btn">
              Login
            </button>
          </form>
          
          <div className="admin-login-footer">
            <p>Protected area - Admin access only</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Admin;
